import { useState, useContext, useRef, useEffect } from 'react';
import { AppContext } from '../util/AppContext';
import { getConfigValue } from '../util/getConfigValue';
import PropType from 'prop-types';
import useComponentVisible from '../hooks/useComponentVisible';
import { getSanitizedHtml } from '../util/getSanitizedHtml';
import RestrictionModal from './modals/RestrictionModal';
import FormularyModal from './modals/FormularyModal';
import SpecialtyPharmacyModal from './modals/SpecialtyPharmacyModal'
import Tooltip from './Tooltip';
import PlanListInlineIcon from './PlanListInlineIcon';
import { planListIconType } from '../util/constants';

PlanListRowModal.propTypes = {
    plan: PropType.shape({
        data: PropType.shape({
            name: PropType.string,
            benType: PropType.string,
            isPrintable: PropType.bool,
            planClass: PropType.string,
            isWin: PropType.bool,
            formularyRestriction: PropType.string,
            planId: PropType.string,
            formularyDrillDown: PropType.arrayOf(
                PropType.shape({
                    formulary: PropType.string,
                    coverage: PropType.string,
                    livesPercentage: PropType.number,
                })
            ),
            specialtyPharmacies: PropType.array,
            minPrintPlans: PropType.number,
            productId: PropType.number,
            competitive: PropType.arrayOf(
                PropType.shape({
                    productId: PropType.number
                })
            )
        }),
    }),
    formularyDrillDownEnabled: PropType.bool,
    restrictionDrillDownEnabled: PropType.bool,
    specialtyPharmacyDrillDownEnabled: PropType.bool,
    onOptionsClick: PropType.func,
    index: PropType.number,
    canSelectFormulary: PropType.bool,
    canSelectRestrictions: PropType.bool,
    canSelectSpecialtyPharmacy: PropType.bool,
    includeRestrictions: PropType.bool,
    setIncludeRestrictions: PropType.func,
    includeSpecialty: PropType.bool,
    setIncludeSpecialty: PropType.func,
    includeFormularies: PropType.bool,
    setIncludeFormularies: PropType.func,
    includeCompetitiveRestrictionNotes: PropType.array,
    setIncludeCompetitiveRestrictionNotes: PropType.func,
    enableEllipsisButton: PropType.bool,
    enableInlineIcons: PropType.bool,
}

function PlanListRowModal({
    plan,
    value,
    index,
    onOptionsClick,
    formularyDrillDownEnabled,
    restrictionDrillDownEnabled,
    specialtyPharmacyDrillDownEnabled,
    canSelectFormulary,
    canSelectRestrictions,
    canSelectSpecialtyPharmacy,
    includeRestrictions,
    includeSpecialty,
    includeFormularies,
    includeCompetitiveRestrictionNotes,
    setIncludeRestrictions,
    setIncludeFormularies,
    setIncludeSpecialty,
    setIncludeCompetitiveRestrictionNotes,
    enableEllipsisButton,
    enableInlineIcons }) {
    const [appState] = useContext(AppContext);
    const [optionsMenuRef, optionsMenuIsVisible, setOptionsMenuIsVisible] = useComponentVisible(false);
    const [infoModalLeft, setInfoModalLeft] = useState('0px');
    const [infoModalTop, setInfoModalTop] = useState('0px');

    const { formularyDrillDown, specialtyPharmacies } = plan.data;
    const [formularyRestriction, setFormularyRestriction] = useState(plan.data.formularyRestriction);

    const [showRestrictionModal, setShowRestrictionModal] = useState(false);
    const [showFormularyModal, setShowFormularyModal] = useState(false);
    const [showSpecialtyPharmacy, setShowSpecialtyPharmacy] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState(plan.data.productId);
    const [isFormularyDrillDownEnabled, setIsFormularyDrillDownEnabled] = useState(formularyDrillDownEnabled);
    const [isSpecialtyPharmacyDrillDownEnabled, setIsSpecialtyPharmacyDrillDownEnabled] = useState(specialtyPharmacyDrillDownEnabled);
    const tooltipTarget = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const showEllipsisButton = (formularyDrillDownEnabled && formularyDrillDown && formularyDrillDown.length > 0) || (restrictionDrillDownEnabled && formularyRestriction && formularyRestriction.length > 0) || (specialtyPharmacyDrillDownEnabled && specialtyPharmacies && specialtyPharmacies.length > 0);
    
    useEffect(() => {
        const foundProductId = findProductFromValue();
        if (foundProductId) {
            setSelectedProductId(foundProductId);
            setFormularyRestriction(plan.data.formularyRestriction);
            setIsFormularyDrillDownEnabled(formularyDrillDownEnabled);
            setIsSpecialtyPharmacyDrillDownEnabled(specialtyPharmacyDrillDownEnabled);

            const parsedFoundProductId = parseInt(foundProductId, 10);
            if (parsedFoundProductId !== plan.data.productId) {
                // update the modal to display the correct competitive message
                const competitive = plan.data?.competitive;
                if (Array.isArray(competitive)) {
                    const child = competitive.find(e => e.productId === parsedFoundProductId);
                    if (child?.formularyRestriction) {
                        setFormularyRestriction(child.formularyRestriction);
                    }
                }
                setIsFormularyDrillDownEnabled(false);
                setIsSpecialtyPharmacyDrillDownEnabled(false);
            } 
        }
    }, [value, plan.data, formularyDrillDownEnabled, specialtyPharmacyDrillDownEnabled]);

    function handleInfoClick(e) {
        getModalPosition(e);
        setOptionsMenuIsVisible(true);
    }

    function getModalPosition(e) {
        const SCREEN_PADDING = 12;

        const triggerEl = e.currentTarget;
        const buttonRect = triggerEl.getBoundingClientRect();
        const parent = triggerEl.closest('.plan-column-options');
        const modalPopup = parent.querySelector('.pd-plan-options');

        // Measure modal dimensions (element is always in layout via visibility: hidden)
        const modalRect = modalPopup?.getBoundingClientRect();
        const modalWidth = modalRect?.width || window.innerWidth;
        const modalHeight = modalRect?.height || getModalHeight();

        // The modal's top/left are relative to its offsetParent
        const offsetParent = modalPopup?.offsetParent || parent;
        const offsetRect = offsetParent.getBoundingClientRect();

        // Find the nearest scrollable ancestor to determine the visible area
        let scrollContainer = triggerEl.parentElement;
        while (scrollContainer && scrollContainer !== document.documentElement) {
            const { overflowY } = getComputedStyle(scrollContainer);
            if (overflowY === 'auto' || overflowY === 'scroll')  {
                break;
            }
            scrollContainer = scrollContainer.parentElement;
        }
        const isScrollable = scrollContainer && scrollContainer !== document.documentElement;
        const visibleTop = isScrollable ? Math.max(scrollContainer.getBoundingClientRect().top, 0) : 0;
        const visibleBottom = isScrollable ? Math.min(scrollContainer.getBoundingClientRect().bottom, window.innerHeight) : window.innerHeight;

        // --- Vertical: prefer below the button, flip above if not enough room ---
        const spaceBelow = visibleBottom - buttonRect.bottom;
        const spaceAbove = buttonRect.top - visibleTop;
        const buttonMidY = buttonRect.top + buttonRect.height / 2;
        const showBelow = buttonMidY < (visibleTop + visibleBottom) / 2
            ? spaceBelow >= modalHeight || spaceBelow >= spaceAbove  // top-half: prefer below unless above has more room
            : spaceBelow >= modalHeight;                              // bottom-half: only below if it fits

        let topInViewport = showBelow
            ? buttonRect.bottom - SCREEN_PADDING
            : buttonRect.top - modalHeight - SCREEN_PADDING;

        // Clamp within the visible scroll area
        topInViewport = Math.max(visibleTop + SCREEN_PADDING, Math.min(topInViewport, visibleBottom - modalHeight - SCREEN_PADDING));

        // --- Horizontal: left-align or right-align based on button position ---
        const leftInViewport = buttonRect.left + buttonRect.width / 2 < window.innerWidth / 2
            ? buttonRect.left    // left-side button → align left edges
            : buttonRect.right - modalWidth; // right-side button → align right edges

        // Clamp within the viewport
        const clampedLeft = Math.max(SCREEN_PADDING, Math.min(leftInViewport, window.innerWidth - modalWidth - SCREEN_PADDING));

        // Convert viewport coordinates to offsetParent-relative values
        setInfoModalTop(`${topInViewport - offsetRect.top + offsetParent.scrollTop}px`);
        setInfoModalLeft(`${clampedLeft - offsetRect.left + offsetParent.scrollLeft}px`);
    }

    function handleEllipsisClick(e) {
        getModalPosition(e);
        setOptionsMenuIsVisible(!optionsMenuIsVisible);
    }

    function getModalHeight() {
        const hasformularies = (isFormularyDrillDownEnabled && formularyDrillDown && formularyDrillDown.length > 0) || false;
        const hasRestrictions = (restrictionDrillDownEnabled && formularyRestriction && formularyRestriction.length > 0) || false;
        const hasSpecilaityPharmacy = (isSpecialtyPharmacyDrillDownEnabled && specialtyPharmacies && specialtyPharmacies.length > 0) || false;
        const count = [hasformularies, hasRestrictions, hasSpecilaityPharmacy].filter(e => e === true).length;

        switch (count) {
            case 3:
                return 330;
            case 2:
                return 240;
            default:
                return 150;
        }
    }

    function findProductFromValue() {
        if(!value) {
            return null;
        }
        let item = value.split(' ').find(e => e.startsWith('data-product-id='));
        if (item) {
            return parseInt(item.split('=')[1].replace("'", ''));
        }

        return null;
    }

    function handleIncludeRestrictionsClick(include) {
        //only set the state if the onOptionsClick function returns true
        if (isPlanLevelProduct()) {
            if (onOptionsClick({ ...plan.data, includeRestrictions: include, includeFormularies: includeFormularies, includeSpecialtyPharmacy: includeSpecialty, includeCompetitiveRestrictionNotes: includeCompetitiveRestrictionNotes })) {
                setIncludeRestrictions(include);
            }
        }
        else {
            const competitiveOption = {
                productId: findProductFromValue(),
                includeRestrictions: include
            };

            let competitiveOptions = includeCompetitiveRestrictionNotes.filter(e => e.productId !== competitiveOption.productId);
            competitiveOptions.push(competitiveOption);

            if (onOptionsClick({ ...plan.data, includeRestrictions: includeRestrictions, includeFormularies: includeFormularies, includeSpecialtyPharmacy: includeSpecialty, includeCompetitiveRestrictionNotes: competitiveOptions })) {
                setIncludeCompetitiveRestrictionNotes(competitiveOptions);
            }
        }
    }

    function isCompetitiveProductSelected() {
        if (!includeCompetitiveRestrictionNotes?.length > 0) {
            return false;
        }

        return includeCompetitiveRestrictionNotes.some(e => e.productId === selectedProductId && e.includeRestrictions);
    }

    function isPlanLevelProduct() {
        return selectedProductId === plan.data.productId;
    }

    function isRestrictionSelected(){
        return isPlanLevelProduct() ? includeRestrictions : isCompetitiveProductSelected();
    }

    function handleIncludeFormulariesClick(include) {
        //only set the state if the onOptionsClick function returns true
        if (onOptionsClick({ ...plan.data, includeFormularies: include, includeRestrictions: includeRestrictions, includeSpecialtyPharmacy: includeSpecialty, includeCompetitiveRestrictionNotes: includeCompetitiveRestrictionNotes })) {
            setIncludeFormularies(include);
        }
    }

    function handleIncludeSpecialtyClick(include) {
        //only set the state if the onOptionClick returns true
        if (onOptionsClick({ ...plan.data, includeSpecialtyPharmacy: include, includeFormularies: includeFormularies, includeRestrictions: includeRestrictions, includeCompetitiveRestrictionNotes: includeCompetitiveRestrictionNotes })) {
            setIncludeSpecialty(include);
        }
    }

    const optionsMenu = (
        <>{/* <!-- Options buttons and check boxes --> */}
            <div ref={optionsMenuRef} className='pd-plan-col pd-plan-options col-12 mb-3' id='options-row-1'
                style={{
                    visibility: optionsMenuIsVisible ? 'visible' : 'hidden',
                    pointerEvents: optionsMenuIsVisible ? 'auto' : 'none',
                    left: infoModalLeft, 
                    top: infoModalTop, 
                    position: 'absolute'
                }}>
                <div className='row justify-content-center align-items-center'>
                    <div className='col-10 py-3'>
                        {isFormularyDrillDownEnabled && formularyDrillDown && formularyDrillDown.length > 0 && (
                            <>
                                <h3 className='type-16 type-bold'>{getConfigValue(appState.config?.PlanList?.Options?.Formularies, appState)?.Value}</h3>
                                <div className='d-flex justify-content-between align-items-center mb-5'>
                                    <button
                                        className='btn btn-outline-primary btn-icon btn-sm d-flex align-items-center'
                                        onClick={() => setShowFormularyModal(true)}>
                                        {getConfigValue(appState.config?.PlanList?.Options?.ShowList?.Label, appState)?.Value}{' '}
                                        <span className='fa-regular fa-chevron-right btn-icon'></span>
                                    </button>
                                    <div>
                                        <div className={`form-check ${canSelectFormulary ? '' : 'disabled'}`}>
                                            <input
                                                className='form-check-input'
                                                type='checkbox'
                                                checked={includeFormularies}
                                                onChange={(e) => handleIncludeFormulariesClick(e.target.checked)}
                                                disabled={!canSelectFormulary}
                                                aria-disabled={!canSelectFormulary}
                                                id={`planlist-input-options-formularies-row${index}`}
                                                data-plan-alert={`planlist-plan-alert-row${index}`}
                                            />
                                            <label className='form-check-label type-14' htmlFor={`planlist-input-options-formularies-row${index}`}>
                                                {getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.Label, appState)?.Value}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        {restrictionDrillDownEnabled && formularyRestriction && formularyRestriction.length > 0 && (
                            <>
                                <h3 className='type-16 type-bold'>{getConfigValue(appState.config?.PlanList?.Options?.Restrictions, appState)?.Value}</h3>
                                <div className='d-flex justify-content-between align-items-center mb-5'>
                                    <button
                                        className='btn btn-outline-primary btn-icon btn-sm d-flex align-items-center'
                                        onClick={() => setShowRestrictionModal(true)}>
                                        {getConfigValue(appState.config?.PlanList?.Options?.ShowList?.Label, appState)?.Value}{' '}
                                        <span className='fa-regular fa-chevron-right btn-icon'></span>
                                    </button>
                                    <div>
                                        <div className={`form-check ${canSelectRestrictions ? '' : 'disabled'}`}>
                                            <input
                                                className='form-check-input'
                                                type='checkbox'
                                                checked={isRestrictionSelected()}
                                                onChange={(e) => handleIncludeRestrictionsClick(e.target.checked)}
                                                disabled={!canSelectRestrictions}
                                                aria-disabled={!canSelectRestrictions}
                                                id={`planlist-input-options-restrictions-row${index}`}
                                                data-plan-alert={`planlist-plan-alert-row${index}`}
                                            />
                                            <label className='form-check-label type-14' htmlFor={`planlist-input-options-restrictions-row${index}`}>
                                                {getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.Label, appState)?.Value}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        {isSpecialtyPharmacyDrillDownEnabled && specialtyPharmacies && specialtyPharmacies.length > 0 && (
                            <>
                                <h3 className='type-16 type-bold'>{getConfigValue(appState.config?.PlanList?.Options?.SpecialtyPharmacy, appState)?.Value}</h3>
                                <div className='d-flex justify-content-between align-items-center mb-5'>
                                    <button
                                        className='btn btn-outline-primary btn-icon btn-sm d-flex align-items-center'
                                        onClick={() => setShowSpecialtyPharmacy(true)}>
                                        {getConfigValue(appState.config?.PlanList?.Options?.ShowList?.Label, appState)?.Value}{' '}
                                        <span className='fa-regular fa-chevron-right btn-icon'></span>
                                    </button>
                                    <div>
                                        <div className={`form-check ${canSelectSpecialtyPharmacy ? '' : 'disabled'}`}>
                                            <input
                                                className='form-check-input'
                                                type='checkbox'
                                                checked={includeSpecialty}
                                                onChange={(e) => handleIncludeSpecialtyClick(e.target.checked)}
                                                disabled={!canSelectSpecialtyPharmacy}
                                                aria-disabled={!canSelectSpecialtyPharmacy}
                                                id={`planlist-input-options-specialtypharm-row${index}`}
                                                data-plan-alert={`planlist-plan-alert-row${index}`}
                                            />
                                            <label className='form-check-label type-14' htmlFor={`planlist-input-options-specialtypharm-row${index}`}>
                                                {getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.Label, appState)?.Value}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                    </div>
                </div>
            </div>

            {/* <!-- Button - options --> */}
        </>
    );



    return (
        <div className='pd-plan-table-cell-complex'>
            <div
                onMouseOver={() => setShowTooltip(true)}
                onFocus={() => setShowTooltip(true)}
                onMouseOut={() => setShowTooltip(false)}
                onBlur={() => setShowTooltip(false)}
                style={{ color: optionsMenuIsVisible ? 'cadetblue' : 'inherit' }}
                onClick={e => handleInfoClick(e)}
                className='pd-plan-table-value'>
                <span
                    ref={tooltipTarget}
                    style={{ display: 'inline' }}
                    dangerouslySetInnerHTML={getSanitizedHtml(value)}>
                </span>
            </div>
            {enableInlineIcons && (
                <div className='pd-plan-table-options'>
                    <PlanListInlineIcon
                        showIcon={isFormularyDrillDownEnabled && formularyDrillDown && formularyDrillDown.length > 0}
                        iconType={planListIconType.Formulary}
                        isSelected={includeFormularies}
                        onClick={() => handleIncludeFormulariesClick(!includeFormularies)}
                        index={index}
                        enabled={canSelectFormulary}
                    />
                    <PlanListInlineIcon
                        showIcon={restrictionDrillDownEnabled && formularyRestriction && formularyRestriction.length > 0}
                        iconType={planListIconType.RestrictionNotes}
                        isSelected={isRestrictionSelected()}
                        onClick={() => handleIncludeRestrictionsClick(!isRestrictionSelected())}
                        index={index}
                        enabled={canSelectRestrictions}
                    />
                    <PlanListInlineIcon
                        showIcon={isSpecialtyPharmacyDrillDownEnabled && specialtyPharmacies && specialtyPharmacies.length > 0}
                        iconType={planListIconType.SpecialtyPharmacy}
                        isSelected={includeSpecialty}
                        onClick={() => handleIncludeSpecialtyClick(!includeSpecialty)}
                        index={index}
                        enabled={canSelectSpecialtyPharmacy}
                    />
                </div>
            )}

            <Tooltip
                text={`${getConfigValue(appState.config?.PlanList?.HasAdditionalInfo.Tooltip, appState)?.Value}`}
                placement='left'
                ref={tooltipTarget}
                showTooltip={showTooltip}
                index={index}
                modifiers={[
                    { name: 'flip', options: { fallbackPlacements: ['right', 'top', 'bottom'] } },
                    { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } }
                ]}
            />

            <RestrictionModal show={showRestrictionModal} setShow={setShowRestrictionModal} plan={plan} productId={selectedProductId} />
            <FormularyModal show={showFormularyModal} setShow={setShowFormularyModal} plan={plan} />
            <SpecialtyPharmacyModal show={showSpecialtyPharmacy} setShow={setShowSpecialtyPharmacy} plan={plan} />

            <div className='col-sm d-flex justify-content-end'>
                {enableEllipsisButton && showEllipsisButton && (
                    <>
                        <div className='d-flex flex-column'>
                            <button
                                className='btn btn-outline-primary btn-md btn-icon d-lg-none'
                                onClick={(e) => handleEllipsisClick(e)}
                                data-bs-toggle='collapse'
                                data-bs-target={`#options-row-${index}`}
                                aria-expanded={optionsMenuIsVisible}
                                aria-controls={`options-row-${index}`}>
                                {getConfigValue(appState.config?.PlanList?.Options?.Label, appState)?.Value}{' '}
                                <span className='fa-regular fa-ellipsis btn-icon'></span>
                            </button>
                        </div>
                        <button
                            className='btn btn-ghost-secondary btn-sm btn-icon-only d-none d-lg-flex'
                            onClick={(e) => handleEllipsisClick(e)}
                            data-bs-toggle='collapse'
                            data-bs-target={`#options-row-${index}`}
                            aria-expanded={optionsMenuIsVisible}
                            aria-controls={`options-row-${index}`}>
                            <span className='fa-regular fa-ellipsis btn-icon'></span>
                        </button>
                    </>
                )}
                {optionsMenu}
            </div>

        </div>
    )
}

export default PlanListRowModal;